import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  appointmentTypeService,
  AppointmentType,
  CreateAppointmentTypeDto,
  UpdateAppointmentTypeDto,
  DeleteAppointmentTypeDto,
} from '../services/appointmentTypeService';
import { queryKeys } from '../config/queryKeys';

interface UseAppointmentTypesParams {
  businessId?: string;
  enabled?: boolean;
}

export function useAppointmentTypes(params: UseAppointmentTypesParams = {}) {
  const { businessId, enabled = true } = params;

  return useQuery({
    queryKey: queryKeys.appointmentTypes.list(businessId),
    queryFn: () => appointmentTypeService.getAppointmentTypes(businessId),
    enabled,
    // Longer stale time since types rarely change
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    // Return stale data immediately while revalidating in background
    refetchOnMount: 'always', // Always check for updates on mount
  });
}

export function useCreateAppointmentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dto, businessId }: { dto: CreateAppointmentTypeDto; businessId?: string }) =>
      appointmentTypeService.createAppointmentType(dto, businessId),
    onSuccess: () => {
      // Invalidate all appointment type lists
      queryClient.invalidateQueries({ queryKey: queryKeys.appointmentTypes.all });
    },
  });
}

export function useUpdateAppointmentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateAppointmentTypeDto }) =>
      appointmentTypeService.updateAppointmentType(id, dto),
    onSuccess: (updatedType) => {
      // Update the specific type in cache
      queryClient.setQueryData(
        queryKeys.appointmentTypes.detail(updatedType.id),
        updatedType
      );
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.appointmentTypes.all });
    },
  });
}

export function useDeleteAppointmentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: DeleteAppointmentTypeDto }) =>
      appointmentTypeService.deleteAppointmentType(id, dto),
    onSuccess: (_, { id }) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.appointmentTypes.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.appointmentTypes.all });
      // Also invalidate appointments since they reference types
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
    },
  });
}
